/* ==========================================================================
   Welch & Co — Title Case Product Names
   --------------------------------------------------------------------------
   Rewrites ALL-CAPS product titles into proper title case on the frontend.

   Why: Client stores titles as "PRE-OWNED 18CT GOLD GROOVED DIAMOND BAND RING"
   (WooCommerce import habit). On a luxury jewellery site this reads as shouty
   and off-brand. Pure CSS (`text-transform: capitalize` or `::first-letter`)
   can't produce real title case — it breaks on hyphens, doesn't know small
   words, doesn't know brand names, can't preserve "9ct" / "18ct" / "II".

   This script does: "Pre-Owned 18ct Gold Grooved Diamond Band Ring",
                     "Vintage Clogau Gold Tree of Life",
                     "Georg Jensen Sterling Silver Bracelet".

   Scope: frontend DOM only. Does NOT affect checkout, order confirmation
   emails, meta titles, Open Graph tags, structured data, Google Shopping
   feed, or the Shopify admin. For those, the only real fix is a CSV
   re-import with cleaned titles in Shopify Products → Export/Import.

   Reversible: delete this file + its <script> tag in snippets/head.liquid,
   and remove the `:not(.wc-tc-done)` CSS fallback in welch-overrides.css.

   Performance notes:
   - Uses a single querySelectorAll with a pre-built selector string.
   - `isAllCaps` uses charCodeAt in a tight loop (faster than regex for
     short strings) and short-circuits on the first lowercase letter.
   - Results are memoised by adding a `.wc-tc-done` class to processed
     elements. The class also serves as the CSS fallback gate — CSS
     lowercases until JS marks the node, eliminating the ALL-CAPS flash.
   - MutationObserver batches additions via requestAnimationFrame so
     cart drawer ajax updates are processed once per frame, not per node.
   - A TreeWalker touches only text nodes, preserving nested <a> links.
   ========================================================================== */
(function () {
  'use strict';

  var SELECTOR = [
    '.product__title',
    '.product-quick-add__form .product__title',
    '.product-item__title',
    '.drawer--cart .cart__item__title',
    '.template-cart .cart__item__title',
    '.cart-bar__product__title',
    '.pickup__product__title',
    '.product__notification__title',
    '.product-upsell__title'
  ].join(',');

  var DONE_CLASS = 'wc-tc-done';

  /* Small words stay lowercase unless they are the first word of a title. */
  var SMALL_WORDS = new Set([
    'a', 'an', 'and', 'as', 'at', 'but', 'by', 'for', 'if', 'in', 'of',
    'on', 'or', 'the', 'to', 'vs', 'via', 'with'
  ]);

  /* Multi-word brand names — substituted into the string BEFORE tokenisation
     so that "GEORG JENSEN" is handled as a unit. Add entries here as new
     brands are stocked. Order matters: longer matches first. */
  var BRANDS = [
    ['GEORG JENSEN', 'Georg Jensen'],
    ['VAN CLEEF & ARPELS', 'Van Cleef & Arpels'],
    ['VAN CLEEF', 'Van Cleef'],
    ['TIFFANY & CO', 'Tiffany & Co'],
    ['TIFFANY AND CO', 'Tiffany & Co'],
    ['PATEK PHILIPPE', 'Patek Philippe'],
    ['AUDEMARS PIGUET', 'Audemars Piguet'],
    ['TAG HEUER', 'TAG Heuer']
  ];

  /* Single-word brand names — proper-cased where found as whole tokens. */
  var SINGLE_BRANDS = new Set([
    'CLOGAU', 'LALIQUE', 'ROLEX', 'CARTIER', 'OMEGA', 'TIFFANY', 'BULGARI',
    'BOUCHERON', 'CHOPARD', 'PIAGET', 'BREITLING', 'GUCCI', 'HERMES',
    'CHANEL', 'DIOR', 'PRADA', 'MOVADO', 'SEIKO', 'LONGINES', 'IWC',
    'TUDOR', 'FABERGE'
  ]);

  /* Acronyms that should stay fully uppercase. */
  var ACRONYMS = new Set(['UK', 'USA', 'US', 'EU', 'BC', 'AD', 'AM', 'PM']);

  /* Weight/purity markers that should stay lowercase:
     9ct, 18k, 14kt, 2.00cts, 0.50ct, 1.90cts. */
  var WEIGHT_RE = /^\d+(\.\d+)?(cts?|kt?)$/i;

  /* Tokens that start with digits and end with letters — ordinals (20th, 1st,
     2nd, 3rd) and decade markers (1960s, 70s). Keep the letters lowercase. */
  var NUM_SUFFIX_RE = /^\d+[a-z]+$/i;

  /* Roman numerals II–XII — stay uppercase (common in regnal/watch names). */
  var ROMAN_RE = /^(ii|iii|iv|v|vi|vii|viii|ix|x|xi|xii)$/i;

  /* Regex escape for dynamic brand regex construction. */
  function escapeRe(s) {
    return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  /* Lowercase the whole word, then uppercase the first *alphabetic* char.
     This handles leading punctuation like curly quotes: `"tree` → `"Tree`. */
  function properCase(word) {
    var lower = word.toLowerCase();
    for (var i = 0; i < lower.length; i++) {
      var c = lower.charCodeAt(i);
      if (c >= 97 && c <= 122) {
        return lower.slice(0, i) + lower.charAt(i).toUpperCase() + lower.slice(i + 1);
      }
    }
    return lower;
  }

  function titleCaseWord(word, isFirst) {
    if (!word) return word;
    var upper = word.toUpperCase();
    var lower = word.toLowerCase();

    if (ACRONYMS.has(upper)) return upper;
    if (WEIGHT_RE.test(word)) return lower;
    if (NUM_SUFFIX_RE.test(word)) return lower;
    if (ROMAN_RE.test(word)) return upper;
    if (SINGLE_BRANDS.has(upper)) return properCase(word);
    if (!isFirst && SMALL_WORDS.has(lower)) return lower;

    /* Compound words joined by - or / : title-case EACH part independently.
       "CLIP-ON" → "Clip-On", "PRE-OWNED" → "Pre-Owned",
       "1950/1960S" → "1950/1960s" (via NUM_SUFFIX_RE on each part). */
    if (word.indexOf('-') !== -1 || word.indexOf('/') !== -1) {
      var parts = word.split(/([-/])/);
      for (var i = 0; i < parts.length; i++) {
        if (parts[i] === '-' || parts[i] === '/') continue;
        parts[i] = titleCaseWord(parts[i], true);
      }
      return parts.join('');
    }

    return properCase(word);
  }

  function titleCase(str) {
    if (!str) return str;
    var s = str;

    /* Pre-substitute multi-word brand names so tokenisation keeps them intact. */
    for (var i = 0; i < BRANDS.length; i++) {
      var upper = BRANDS[i][0];
      var proper = BRANDS[i][1];
      if (s.toUpperCase().indexOf(upper) !== -1) {
        s = s.replace(new RegExp(escapeRe(upper), 'gi'), proper);
      }
    }

    /* Split on whitespace, preserving whitespace tokens so we can rejoin. */
    var tokens = s.split(/(\s+)/);
    var firstWordSeen = false;
    for (var j = 0; j < tokens.length; j++) {
      var tok = tokens[j];
      if (!tok) continue;
      if (tok.charCodeAt(0) === 32 || /^\s+$/.test(tok)) continue;

      /* Skip tokens that are already mixed case — came from brand substitution. */
      if (tok !== tok.toUpperCase() && tok !== tok.toLowerCase()) {
        firstWordSeen = true;
        continue;
      }

      tokens[j] = titleCaseWord(tok, !firstWordSeen);
      firstWordSeen = true;
    }

    return tokens.join('');
  }

  /* "Shouty" string detector — ratio-based so trailing tokens like "1960s"
     or parentheticals like "(plus shoulder diamonds)" don't disqualify
     a string that is otherwise ALL CAPS. A string counts as shouty if
     it has ≥3 uppercase letters AND uppercase letters dominate lowercase
     at a ratio of at least 2:1. 2:1 is the sweet spot: real shouty titles
     run 2.5:1 to infinity (pure caps), while mixed-case input is almost
     always <0.3:1 upper to lower — no ambiguous middle ground seen in
     the Welch product corpus (250 titles surveyed 2026-04-09).
     Loop is char-code based to avoid regex allocation on every text node. */
  function isShouty(s) {
    if (!s) return false;
    var upper = 0, lower = 0;
    for (var i = 0; i < s.length; i++) {
      var c = s.charCodeAt(i);
      if (c >= 65 && c <= 90) upper++;
      else if (c >= 97 && c <= 122) lower++;
    }
    return upper >= 3 && upper >= lower * 2;
  }

  /* Process one target element: walk its text nodes, rewrite ALL-CAPS ones
     in place, then mark the element as done so CSS fallback stops applying. */
  function processNode(node) {
    if (!node || node.classList.contains(DONE_CLASS)) return;
    var walker = document.createTreeWalker(node, NodeFilter.SHOW_TEXT, null);
    var textNode;
    while ((textNode = walker.nextNode())) {
      var t = textNode.nodeValue;
      if (isShouty(t)) textNode.nodeValue = titleCase(t);
    }
    node.classList.add(DONE_CLASS);
  }

  function process(root) {
    if (!root || !root.querySelectorAll) return;
    if (root.matches && root.matches(SELECTOR)) processNode(root);
    var nodes = root.querySelectorAll(SELECTOR);
    for (var i = 0; i < nodes.length; i++) processNode(nodes[i]);
  }

  /* Initial pass on the loaded document. `defer` on the <script> tag
     guarantees DOM is parsed and document.body exists by the time we run. */
  process(document);

  /* Watch for dynamically added nodes: cart drawer ajax, quick buy, search
     dropdown, upsell modals. Batch with rAF so we process once per frame. */
  var pending = new Set();
  var scheduled = false;

  function flush() {
    pending.forEach(process);
    pending.clear();
    scheduled = false;
  }

  var observer = new MutationObserver(function (mutations) {
    for (var i = 0; i < mutations.length; i++) {
      var added = mutations[i].addedNodes;
      for (var j = 0; j < added.length; j++) {
        var n = added[j];
        if (n.nodeType === 1) pending.add(n);
      }
    }
    if (!scheduled && pending.size) {
      scheduled = true;
      requestAnimationFrame(flush);
    }
  });

  observer.observe(document.body, { childList: true, subtree: true });
})();
